

import { useRef, useCallback, useState } from 'react'
import { toast } from 'sonner'

/**
 * Extracts a downloadable URL and file name from a storyboard item.
 * Mirrors the logic in StoryboardCanvas.getItemMedia().
 */
function getItemDownloadInfo(item) {
    if (!item) return null

    let url = null
    let mediaType = null
    let fileName = null

    switch (item.type) {
        case 'asset': {
            const a = item.asset
            if (!a?.url) return null
            url = a.url
            mediaType = a.mediaType
            fileName = a.name || a.originalFilename
            break
        }
        case 'generation': {
            const gen = item.generation
            if (!gen?.url) return null
            url = gen.url
            mediaType = gen.type
            fileName = gen.name
            break
        }
        case 'url': {
            if (!item.url) return null
            url = item.url
            mediaType = item.mediaType
            break
        }
        case 'sketch': {
            url = item.sketchCompositeUrl || item.url
            if (!url) return null
            mediaType = 'image'
            break
        }
        default:
            return null
    }

    if (!url) return null
    if (!mediaType || (mediaType !== 'image' && mediaType !== 'video' && mediaType !== 'audio')) return null

    // Derive file name
    if (!fileName) {
        fileName = item.name || getFilenameFromUrl(url) || 'asset'
    }

    return { url, fileName, mediaType }
}

function getFilenameFromUrl(url) {
    if (!url || typeof url !== 'string') return ''
    try {
        const pathname = new URL(url).pathname
        const segments = pathname.split('/')
        return segments[segments.length - 1] || ''
    } catch {
        return ''
    }
}

/**
 * Hook to export all storyboard assets as a ZIP via a background Web Worker.
 * Reports progress via sonner toast.
 */
export default function useStoryboardExport() {
    const workerRef = useRef(null)
    const toastIdRef = useRef(null)
    const lastToastUpdateRef = useRef(0)
    const [exporting, setExporting] = useState(false)

    const exportStoryboard = useCallback((storyboard, segment, exportName) => {
        // Collect assets per element (may be empty if no panels exist)
        const elements = (storyboard?.elements || []).map((el, idx) => {
            const items = []
            for (const item of (el.items || [])) {
                const info = getItemDownloadInfo(item)
                if (info) items.push(info)
            }
            return {
                name: el.name || `Panel ${idx + 1}`,
                items,
            }
        }).filter(el => el.items.length > 0)

        // Collect script audio files with zero-padded index to preserve order
        const scriptAudios = []
        let audioIndex = 1
        if (segment?.scenes) {
            for (const scene of segment.scenes) {
                for (const script of (scene.script || [])) {
                    if (script.audio_url) {
                        const charName = script.character?.name || 'Unknown'
                        const speechPreview = (script.speech || '').slice(0, 30).replace(/[^a-zA-Z0-9 ]/g, '').trim()
                        const padded = String(audioIndex).padStart(3, '0')
                        scriptAudios.push({
                            name: scene.name || 'Scene',
                            url: script.audio_url,
                            fileName: `${padded} - ${charName} - ${speechPreview || 'audio'}`,
                        })
                        audioIndex++
                    }
                }
            }
        }

        const totalFiles = elements.reduce((sum, el) => sum + el.items.length, 0) + scriptAudios.length

        if (totalFiles === 0) {
            toast.error('No downloadable assets found in this storyboard.')
            return
        }

        setExporting(true)

        // Show initial toast
        toastIdRef.current = toast.loading(`Preparing export... (0/${totalFiles})`, {
            description: 'Downloading assets...',
            duration: Infinity,
        })

        // Terminate any existing worker
        if (workerRef.current) {
            workerRef.current.terminate()
        }

        // Create worker
        const worker = new Worker(
            new URL('../workers/storyboardExportWorker.js', import.meta.url),
            { type: 'module' }
        )
        workerRef.current = worker

        worker.onmessage = (event) => {
            const { type, current, total, message, blob } = event.data

            if (type === 'progress') {
                // Throttle toast updates to avoid React max-update-depth errors
                const now = Date.now()
                if (now - lastToastUpdateRef.current >= 500) {
                    lastToastUpdateRef.current = now
                    toast.loading(`Exporting... (${current}/${total})`, {
                        id: toastIdRef.current,
                        description: message,
                        duration: Infinity,
                    })
                }
            } else if (type === 'done') {
                // Trigger download
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = (exportName || 'storyboard-export') + '.zip'
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(url)

                toast.success('Export complete!', {
                    id: toastIdRef.current,
                    description: `${totalFiles} files downloaded.`,
                    duration: 4000,
                })

                setExporting(false)
                worker.terminate()
                workerRef.current = null
            } else if (type === 'error') {
                toast.error('Export failed', {
                    id: toastIdRef.current,
                    description: message,
                    duration: 5000,
                })
                setExporting(false)
                worker.terminate()
                workerRef.current = null
            }
        }

        worker.onerror = (err) => {
            toast.error('Export failed', {
                id: toastIdRef.current,
                description: err.message || 'An unexpected error occurred.',
                duration: 5000,
            })
            setExporting(false)
            worker.terminate()
            workerRef.current = null
        }

        // Send data to worker
        worker.postMessage({
            elements,
            scriptAudios,
            rootFolder: exportName || 'Storyboard',
        })
    }, [])

    const cancelExport = useCallback(() => {
        if (workerRef.current) {
            workerRef.current.terminate()
            workerRef.current = null
        }
        if (toastIdRef.current) {
            toast.dismiss(toastIdRef.current)
            toastIdRef.current = null
        }
        setExporting(false)
    }, [])

    return { exportStoryboard, cancelExport, exporting }
}
